import { getI18n } from "react-i18next";

type Args = {
  uploadToS3: (arg: File) => void | Promise<void>;
  setUploadError: (arg: string) => void;
  setUploading: (arg: boolean) => void;
};

export default class GoogleDrivePicker {
  uploadToS3: (arg: File) => void;
  setUploadError: (arg: string) => void;
  setUploading: (arg: boolean) => void;
  oauthToken: string;
  pickerShown: boolean;

  constructor(args: Args) {
    this.uploadToS3 = args.uploadToS3;
    this.setUploadError = args.setUploadError;
    this.setUploading = args.setUploading;
    this.oauthToken = "";
    this.pickerShown = false;

    window.gapiLoaded = this.gapiLoaded;
    window.gisLoaded = this.gisLoaded;

    this.loadScripts();
  }

  loadScripts() {
    if (!document.getElementById("google-gapi-script")) {
      const gapiScript = document.createElement("script");
      gapiScript.id = "google-gapi-script";
      gapiScript.src = "https://apis.google.com/js/api.js";
      gapiScript.onload = () => window.gapiLoaded?.();
      gapiScript.async = true;
      document.body.appendChild(gapiScript);
    } else if (window.gapiInitialized) {
      // Script already loaded and initialized; re-register callback in case of re-mount
    }

    if (!document.getElementById("google-gis-script")) {
      const gisScript = document.createElement("script");
      gisScript.id = "google-gis-script";
      gisScript.src = "https://accounts.google.com/gsi/client";
      gisScript.onload = () => window.gisLoaded?.();
      gisScript.async = true;
      document.body.appendChild(gisScript);
    }
  }

  onClick() {
    const i18n = getI18n();
    const t = i18n.getFixedT(null, "job_post", "file_upload");

    if (!window.gapi || !window.gapiInitialized || !window.gisInitialized) {
      console.error("Unable to load Google Drive");
      this.setUploadError(t("try_again", { uploader: "Google Drive" }));
      return;
    }

    window.tokenClient.callback = this.handleAuthResult.bind(this);

    if (this.oauthToken === null) {
      window.tokenClient.requestAccessToken({ prompt: "consent" });
    } else {
      window.tokenClient.requestAccessToken({ prompt: "" });
    }
  }

  handleAuthResult(authResult: { error?: string; access_token: string }) {
    if (authResult && !authResult.error) {
      this.oauthToken = authResult.access_token;
      this.createPicker();
    }
  }

  createPicker() {
    if (!this.pickerShown && this.oauthToken) {
      const picker = new window.google.picker.PickerBuilder()
        .addView(window.google.picker.ViewId.DOCUMENTS)
        .addView(window.google.picker.ViewId.PDFS)
        .setOAuthToken(this.oauthToken)
        .setDeveloperKey(window.ENV.GOOGLE_PICKER_DEVELOPER_KEY)
        .setAppId(window.ENV.GOOGLE_PICKER_APP_ID)
        .setCallback(this.pickerCallback.bind(this))
        .enableFeature(window.google.picker.Feature.SUPPORT_DRIVES)
        .build();

      picker.setVisible(true);
      this.pickerShown = true;
    }
  }

  pickerCallback(data: Record<string, unknown>) {
    switch (data[window.google.picker.Response.ACTION]) {
      case window.google.picker.Action.PICKED:
        this.pickedFile(data);
        this.pickerShown = false;
        break;
      case window.google.picker.Action.CANCEL:
        this.pickerShown = false;
        break;
    }
  }

  pickedFile(data: Record<string, unknown>) {
    const doc = (data[window.google.picker.Response.DOCUMENTS] as Array<Record<string, string>>)[0];
    this.downloadFile({
      id: doc[window.google.picker.Document.ID],
      mimeType: doc[window.google.picker.Document.MIME_TYPE],
      name: doc[window.google.picker.Document.NAME],
      type: doc[window.google.picker.Document.TYPE],
    });
  }

  async downloadFile(metadata: { id: string; mimeType: string; name: string; type: string }) {
    let response;
    let mimeType = metadata.mimeType;
    let filename = metadata.name;

    this.setUploading(true);

    if (metadata.type === "document") {
      response = await window.gapi.client.drive.files.export({
        fileId: metadata.id,
        mimeType: "application/pdf",
      });
      mimeType = "application/pdf";
      filename = filename + ".pdf";
    } else {
      response = await window.gapi.client.drive.files.get({
        fileId: metadata.id,
        supportsAllDrives: true,
        alt: "media",
      });
    }

    const arrayBuffer = this.dataToArrayBuffer(response.body);
    const file = new File([arrayBuffer], filename, { type: mimeType });
    this.uploadToS3(file);
  }

  dataToArrayBuffer(data: string) {
    if (!data) return new Uint8Array(0);
    const arrayBuffer = new Uint8Array(data.length);
    for (let i = 0; i < data.length; i++) {
      arrayBuffer[i] = data.charCodeAt(i);
    }
    return arrayBuffer;
  }

  initializeGapiClient = async () => {
    await window.gapi.client.load("https://www.googleapis.com/discovery/v1/apis/drive/v3/rest");
    window.gapiInitialized = true;
  };

  gapiLoaded = () => {
    window.gapi.load("client:picker", this.initializeGapiClient);
  };

  gisLoaded = () => {
    window.tokenClient = window.google.accounts.oauth2.initTokenClient({
      client_id: window.ENV.GOOGLE_PICKER_CLIENT_ID,
      scope: "https://www.googleapis.com/auth/drive.file",
      callback: () => {},
    });
    window.gisInitialized = true;
  };
}
